import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Shield, Download, FileText, CheckCircle2, Clock, XCircle, MinusCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  'Implemented': { icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  'In Progress': { icon: Clock, cls: 'text-amber-700 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  'Not Started': { icon: XCircle, cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
  'Not Applicable': { icon: MinusCircle, cls: 'text-slate-500 bg-slate-50 border-slate-200', dot: 'bg-slate-400' },
};

export default function AuditorPortalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/submissions/auditor/${token}`);
        setData(res.data);
      } catch (e) {
        setError('Invalid or expired auditor link.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-600">{error || 'Something went wrong'}</p>
      </div>
    </div>
  );

  // Compute summary
  let total = 0, implemented = 0, inProgress = 0, notStarted = 0, na = 0, withEvidence = 0;
  data.matrix.forEach(sec => sec.controls.forEach(c => {
    total++;
    const s = c.status?.toLowerCase() || '';
    if (s === 'implemented') implemented++;
    else if (s === 'in progress') inProgress++;
    else if (s === 'not applicable') na++;
    else notStarted++;
    if (c.evidence_files?.length > 0) withEvidence++;
  }));
  const readyPct = total > 0 ? Math.round(((implemented + na) / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#1a1a2e] text-white py-6 px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Shield className="h-6 w-6 text-[#FF6B35]" />
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>SOC 2 Evidence Matrix</h1>
            </div>
            <p className="text-sm text-slate-300">{data.company_name} | {data.template_name}</p>
          </div>
          <span className="text-xs text-slate-400">Read-Only Auditor View</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-8" data-testid="auditor-summary">
          <SummaryCard label="Ready" value={`${readyPct}%`} />
          <SummaryCard label="Implemented" value={implemented} color="emerald" />
          <SummaryCard label="In Progress" value={inProgress} color="amber" />
          <SummaryCard label="Not Started" value={notStarted} color="red" />
          <SummaryCard label="N/A" value={na} color="slate" />
          <SummaryCard label="With Evidence" value={withEvidence} color="blue" />
        </div>

        {/* Sections */}
        <div className="space-y-6" data-testid="auditor-matrix">
          {data.matrix.map((sec, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-100">
                <h2 className="text-sm font-semibold text-slate-900">{sec.title}</h2>
                {sec.iso_reference && <span className="text-xs text-slate-500">{sec.iso_reference}</span>}
              </div>
              <div className="divide-y divide-slate-100">
                {sec.controls.map((ctrl, j) => {
                  const cfg = statusConfig[ctrl.status] || statusConfig['Not Started'];
                  return (
                    <div key={j} className="p-4" data-testid={`auditor-control-${i}-${j}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="text-sm text-slate-800 font-medium flex-1">{ctrl.label}</p>
                        <span className={`text-xs px-2 py-1 rounded border font-medium flex-shrink-0 ${cfg.cls}`}>{ctrl.status}</span>
                      </div>
                      {ctrl.evidence_files?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs font-medium text-slate-500 mb-1">Evidence Files:</p>
                          {ctrl.evidence_files.map(f => (
                            <a
                              key={f.id}
                              href={`${API_URL}/submissions/auditor/${token}/file/${ctrl.field_name}/${f.id}`}
                              className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 py-1"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FileText className="h-3.5 w-3.5" />
                              {f.filename}
                              <span className="text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  const bg = color ? `bg-${color}-50` : 'bg-white';
  const text = color ? `text-${color}-700` : 'text-slate-900';
  return (
    <div className={`rounded-lg border border-slate-200 p-3 text-center ${bg}`}>
      <p className={`text-2xl font-bold ${text}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
